import { Link } from "@inertiajs/react";
import { useMemo, useState } from "react";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import type { ActivityFeedItem } from "@/types";
import { formatDateTimeAR, formatRelativeTimeAR } from "@/utils/date";

const TYPE_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "purchase_request", label: "Solicitudes" },
  { value: "approval", label: "Aprobaciones" },
  { value: "quote_document", label: "Cotizaciones" },
  { value: "delivery_note", label: "Remitos" },
  { value: "invoice", label: "Facturas" },
];

export function ActivityFeed({ activities }: { activities: ActivityFeedItem[] }) {
  const routes = useOrganizationRoutes();
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return activities.filter((activity) => {
      const typeKey = mapTypeKey(activity);
      if (typeFilter !== "all" && typeKey !== typeFilter) return false;

      if (!needle) return true;

      const searchable = [
        activity.description,
        activity.actor?.full_name,
        activity.actor?.email,
        activity.resource.sector_name,
        activity.resource.supplier_name,
        String(activity.resource.id),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(needle);
    });
  }, [activities, query, typeFilter]);

  if (activities.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        No hay actividad en las últimas 24 horas
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar por solicitud, usuario, sector o proveedor"
            className="sm:max-w-sm"
          />
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="sm:w-48">
              <SelectValue placeholder="Filtrar por tipo" />
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-muted-foreground">
          Mostrando {filtered.length} de {activities.length}
        </div>
      </div>

      {activities.length >= 50 && (
        <div className="text-xs text-muted-foreground">
          Mostramos los últimos 50 eventos. Usá los filtros para encontrar algo
          puntual.
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Fecha</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead className="whitespace-normal">Evento</TableHead>
            <TableHead>Usuario</TableHead>
            <TableHead>Sector / Proveedor</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((activity) => {
            const href = buildActivityHref(activity, routes);
            const description = href ? (
              <Link href={href} className="font-medium hover:underline">
                {activity.description}
              </Link>
            ) : (
              <span className="font-medium">{activity.description}</span>
            );

            return (
              <TableRow key={activity.id}>
                <TableCell className="text-muted-foreground">
                  <span title={formatDateTimeAR(activity.timestamp)}>
                    {formatRelativeTimeAR(activity.timestamp)}
                  </span>
                </TableCell>
                <TableCell>{mapTypeLabel(activity)}</TableCell>
                <TableCell className="whitespace-normal break-words">
                  {description}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {activity.actor?.full_name ?? activity.actor?.email ?? "—"}
                </TableCell>
                <TableCell className="text-muted-foreground whitespace-normal break-words">
                  {activity.resource.sector_name ??
                    activity.resource.supplier_name ??
                    "—"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {filtered.length === 0 && (
        <div className="py-6 text-center text-sm text-muted-foreground">
          No hay resultados con los filtros actuales.
        </div>
      )}
    </div>
  );
}

function mapTypeKey(activity: ActivityFeedItem) {
  if (activity.type.startsWith("approval_")) return "approval";
  if (activity.type.startsWith("quote_document")) return "quote_document";

  return activity.resource.type;
}

function mapTypeLabel(activity: ActivityFeedItem) {
  const key = mapTypeKey(activity);
  const match = TYPE_OPTIONS.find((option) => option.value === key);
  return match?.label ?? "Otro";
}

function buildActivityHref(
  activity: ActivityFeedItem,
  routes: ReturnType<typeof useOrganizationRoutes>,
) {
  switch (activity.resource.type) {
    case "purchase_request":
      return routes.purchaseRequests.show(activity.resource.id);
    case "quote":
      return routes.quotes.show(activity.resource.id);
    case "delivery_note":
      return routes.deliveryNotes.show(activity.resource.id);
    case "invoice":
      return routes.invoices.show(activity.resource.id);
    default:
      return null;
  }
}
